import { h } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { IntlProvider } from 'preact-i18n';
import { FinText, Localizations } from '../i18n';
import { AmDurationType, AmLocations, CmsImageData, Locations, Properties, TravelType, WidgetTab } from '../common';
import { ModalProvider } from '../modal';
import { Actions, actions, BookingState, CompactWidgetState, connectWithStyles } from '../store';
import { WithStyles } from '../style';
import { Navigator, WidgetTestVariant } from '../config';
import * as styles from './BookingWidget.scss';
import Notifications from './Notifications/Notifications';
import RootContainer from './RootContainer/RootContainer';
import { Tabs } from './Tabs/Tabs';
import { Style } from '../style/Style';
import { FlightContent } from './Tabs/TabContent/FlightContent';
import { AMContent } from './Tabs/TabContent/AMContent';
import { Profile, ProfileTier } from '../interfaces';
import { AwardContent } from './Tabs/TabContent/AwardContent/AwardContent';
import { getTabToShow } from '../utils/tabUtils';
import { debounce } from 'lodash';
import { isClient, isServer } from '../utils';

export interface BookingWidgetProps extends Properties {
  embedded: boolean;
  campaignImage?: CmsImageData;
  markLocalizations: boolean;
  renderMainHeading?: boolean;
  testVariant?: WidgetTestVariant;
  enablePersonalization?: boolean;
  enableCompactWidget: boolean;
  enabledTabs: WidgetTab[];
  profile: Profile;
}

export interface BookingWidgetStoreProps {
  localizations: Localizations;
  locations: Locations;
  amLocations: AmLocations;
  currentTab: WidgetTab;
  locale: string;
  travelType: TravelType;
  navigator: Navigator;
  enablePromoCode: boolean;
  compactWidgetState: CompactWidgetState;
}
type EffectiveProps = BookingWidgetProps & WithStyles & BookingWidgetStoreProps & Actions;

const mapStateToProps = ({
  ux: {
    i18n: { localizations },
    compactWidgetState,
  },
  selection: { locations, locale, travelType, activeLocationIndex },
  amState: {
    amSelection: { locations: amLocations },
  },
  currentTab,
  extensionApi: { navigator },
  enablePromoCode,
}: BookingState): BookingWidgetStoreProps => ({
  localizations,
  locations: locations[activeLocationIndex],
  amLocations,
  currentTab,
  locale,
  travelType,
  navigator,
  enablePromoCode,
  compactWidgetState,
});

export const BookingWidget = connectWithStyles<BookingWidgetProps>([styles], { mapStateToProps, actions })(
  ({
    embedded,
    campaignImage,
    renderMainHeading,
    markLocalizations,
    localizations,
    currentTab,
    enabledTabs,
    amLocations,
    tx,
    travelType,
    testVariant = WidgetTestVariant.A,
    profile,
    navigator,
    enablePersonalization,
    setPreviousSearchesEnabled,
    enablePromoCode,
    compactWidgetState,
    enableCompactWidget = true,
  }: EffectiveProps) => {
    const tabToShow = getTabToShow(enabledTabs, currentTab);
    const isOneWay = travelType === TravelType.ONE_WAY;
    const showDurationSelector = tabToShow === WidgetTab.AM && amLocations.holidayType?.type === AmDurationType.LIST;
    const animationElement = useRef<HTMLDivElement>(null);
    const [animationTimeout, setAnimationTimeout] = useState(null);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [dimensions, setDimensions] = useState({
      height: isClient() ? window.innerHeight : null,
      width: isClient() ? window.innerWidth : null,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_continueButtonElement, setContinueButtonElement] = useState<HTMLElement>(undefined);

    useEffect(() => {
      setPreviousSearchesEnabled(enablePersonalization);
    }, [setPreviousSearchesEnabled, enablePersonalization]);

    const navigateToLogin = () => {
      navigator.navigateToLogin();
    };

    const navigateToJoin = () => {
      navigator.navigateToJoin();
    };

    useEffect(() => {
      const handleResize = debounce(() => {
        setDimensions({
          height: window.innerHeight,
          width: window.innerWidth,
        });
      }, 100);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationTimeout) {
          window.clearTimeout(animationTimeout);
          setAnimationTimeout(null);
        }
      };
    });

    useLayoutEffect(() => {
      if (isServer()) {
        return;
      }

      const fn = () => {
        if (!animationElement?.current) {
          return;
        }

        const currentHeight = animationElement.current.clientHeight;
        const gridElement = animationElement.current.getElementsByClassName('bw-selections')[0];

        animationElement.current.style.height = 'auto';

        const desiredHeight = animationElement.current.clientHeight;

        animationElement.current.style.height = `${currentHeight}px`;

        if (currentHeight === desiredHeight) {
          gridElement.classList.add('delayed-fade');
          return;
        }

        if (animationTimeout) {
          window.clearTimeout(animationTimeout);
          setAnimationTimeout(null);
        }

        animationElement.current.classList.add('bw-animating');

        gridElement.classList.remove('delayed-fade');

        setTimeout(() => {
          animationElement.current.style.height = `${desiredHeight}px`;
          gridElement.classList.add('delayed-fade');
        }, 0);

        setTimeout(() => {
          animationElement.current.classList.remove('bw-animating');
        }, 350);
      };

      /**
       * isInitialRender needed because the component renders without CSS initially which causes
       * the height check to be incorrect
       */
      if (isInitialRender) {
        setIsInitialRender(false);
        setTimeout(() => {
          fn();
        }, 0);
      } else {
        fn();
      }
    }, [travelType, compactWidgetState, isInitialRender, dimensions, currentTab, enabledTabs, animationTimeout]);

    return (
      <IntlProvider definition={localizations} mark={markLocalizations}>
        <RootContainer
          campaignImage={campaignImage}
          class={tx('bw-booking-widget', `bw-variant-${testVariant.toLowerCase()}`)}
          embedded={embedded}
        >
          <div
            class={tx('relative', {
              'bw-embedded-content': embedded,
              'pr-xlarge-y': !embedded,
              'bw-horizontal-content': !enableCompactWidget,
              'bw-compact-content': enableCompactWidget,
            })}
          >
            <div class={tx('bw-content-wrapper')}>
              <ModalProvider>
                <div class={tx('bw-animation-wrapper')} ref={animationElement}>
                  <div
                    class={tx('bw-selections contentArea', 'pr-medium rounded-medium', {
                      amMode: tabToShow === WidgetTab.AM,
                      award:
                        tabToShow === WidgetTab.AWARD &&
                        (!profile.isLoggedIn ||
                          profile?.tier === ProfileTier.ACCOUNT ||
                          profile?.tier === ProfileTier.CORPORATE),
                      'promo-code': tabToShow === WidgetTab.FLIGHT && enablePromoCode,
                      'bw-compact-widget-open': compactWidgetState === CompactWidgetState.OPENED,
                      'bw-multicity-layout': travelType === TravelType.MULTI_CITY,
                    })}
                  >
                    <div class={tx('header')}>
                      {renderMainHeading && (
                        <h1 class="nordic-blue-900-text font-heading-2" data-test-title>
                          <FinText id={tabToShow !== WidgetTab.AM ? 'bookingWidget.title' : 'aurinkomatkat.title'} />
                        </h1>
                      )}
                      {!renderMainHeading && (
                        <h2 class="nordic-blue-900-text font-heading-2" data-test-title>
                          <FinText id={tabToShow !== WidgetTab.AM ? 'bookingWidget.title' : 'aurinkomatkat.title'} />
                        </h2>
                      )}
                    </div>

                    <Tabs class={tx('bw-tabs tabs ms-small-b')} enabledTabs={enabledTabs} />

                    {tabToShow === WidgetTab.FLIGHT && (
                      <FlightContent
                        isOneWay={isOneWay}
                        setFocusElement={setContinueButtonElement}
                        testVariant={testVariant}
                      />
                    )}

                    {tabToShow === WidgetTab.AM && (
                      <AMContent
                        showDurationSelector={showDurationSelector}
                        setFocusElement={setContinueButtonElement}
                        testVariant={testVariant}
                      />
                    )}

                    {tabToShow === WidgetTab.AWARD && (
                      <AwardContent
                        testVariant={testVariant}
                        isOneWay={isOneWay}
                        setFocusElement={setContinueButtonElement}
                        profile={profile}
                        navigateToLogin={navigateToLogin}
                        navigateToJoin={navigateToJoin}
                      />
                    )}
                  </div>
                </div>
              </ModalProvider>
              <Notifications class="bw-notifications" currentTab={tabToShow} />
            </div>
          </div>
          <Style />
        </RootContainer>
      </IntlProvider>
    );
  }
);
